import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || process.env.VUE_APP_API_URL || 'http://localhost:3000';

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 seconds - Render free tier can take 30-60s to wake up
});

// Request interceptor - add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('mechtrace_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 (expired token)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('mechtrace_token');
      localStorage.removeItem('mechtrace_user');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }
    }
    return Promise.reject(error);
  }
);

class AuthService {
  constructor() {
    this.tokenKey = 'mechtrace_token';
    this.userKey = 'mechtrace_user';
    this.api = apiClient;
  }

  async register(name, email, password) {
    try {
      const response = await this.api.post('/auth/register', {
        name,
        email,
        password,
      });

      // Don't auto-login after registration - user should sign in manually
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async login(email, password) {
    try {
      const response = await this.api.post('/auth/login', {
        email,
        password,
      });

      if (response.data.access_token) {
        this.setToken(response.data.access_token);
        this.setUser(response.data.user);
      }

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  async getProfile() {
    try {
      const response = await this.api.get('/auth/profile');
      // Update stored user data
      if (response.data) {
        this.setUser(response.data);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await this.api.put('/auth/profile', profileData);
      // Update stored user data
      if (response.data.user) {
        this.setUser(response.data.user);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async changePassword(currentPassword, newPassword) {
    try {
      const response = await this.api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async validateToken() {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const response = await this.api.post('/auth/validate', {});
      return response.data;
    } catch (error) {
      this.logout();
      return null;
    }
  }

  async getAllUsers() {
    try {
      const response = await this.api.get('/auth/users');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Check if token is expired (client-side check)
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }

  // Token management
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  getUser() {
    const user = localStorage.getItem(this.userKey);
    return user ? JSON.parse(user) : null;
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  handleError(error) {
    if (error.response) {
      const message = error.response.data?.message || 'An error occurred';
      return new Error(Array.isArray(message) ? message[0] : message);
    }
    return new Error('Network error. Please try again.');
  }
}

export const authService = new AuthService();
export { apiClient };
export default authService;
